import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  Sparkles,
  Loader2,
  CheckCircle2,
  ShoppingCart,
  HeartPulse,
  MapPin,
  Star,
  Film,
  TrendingUp,
  GraduationCap,
  Briefcase,
  Wrench,
  FileText,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useBulkCreate } from '@/hooks/useBulk';
import { useSites } from '@/hooks/useSites';
import { useFeatures } from '@/hooks/useFeatures';
import { useContentPromptTemplates } from '@/hooks/usePromptTemplates';
import { ApiError } from '@/lib/api-client';
import { cn } from '@/lib/utils';

type CategoryMeta = { id: string; label: string; icon: LucideIcon };
const CATEGORY_META: CategoryMeta[] = [
  { id: 'ecommerce', label: 'E-ticaret / Affiliate', icon: ShoppingCart },
  { id: 'health', label: 'Sağlık / Yaşam', icon: HeartPulse },
  { id: 'travel', label: 'Seyahat / Lokasyon', icon: MapPin },
  { id: 'lifestyle', label: 'Güzellik / Moda', icon: Sparkles },
  { id: 'spiritual', label: 'Mistik', icon: Star },
  { id: 'entertainment', label: 'Eğlence / Medya', icon: Film },
  { id: 'finance', label: 'Finans / Hukuk', icon: TrendingUp },
  { id: 'education', label: 'Eğitim', icon: GraduationCap },
  { id: 'business', label: 'Kurumsal / B2B', icon: Briefcase },
  { id: 'utility', label: 'Utility', icon: Wrench },
  { id: 'blog', label: 'Blog / Genel', icon: FileText },
];

/** Templates that don't make sense in bulk mode — they need more than
 *  just a focus_keyword (URL, X+Y, teams, etc.). We hide them so users
 *  don't pick a template that'll produce weak output. Complex ones
 *  should be created one-by-one via /content/new. */
const BULK_UNSUITABLE = new Set([
  'url_rewrite',
  'comparison',
  'multi_product_compare',
  'match_analysis',
  'text_to_blog',
  'youtube_to_blog',
  'summarize',
  'interview',
  'episode_recap',
]);

export function BulkPage() {
  const navigate = useNavigate();
  const sites = useSites();
  const bulk = useBulkCreate();
  const features = useFeatures();
  const templates = useContentPromptTemplates();

  const [keywordText, setKeywordText] = useState('');
  const [siteId, setSiteId] = useState<string>('');
  const [templateType, setTemplateType] = useState('seo_blog');
  const [selectedCategory, setSelectedCategory] = useState<string>('blog');
  const [sectionLength, setSectionLength] = useState<'short' | 'medium' | 'long'>('medium');
  const [tone, setTone] = useState('Profesyonel');
  const [audience, setAudience] = useState('Genel okuyucu');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ created: number; hint: string } | null>(null);

  const keywords = keywordText
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

  const bulkSuitableTemplates = useMemo(
    () => (templates.data?.templates ?? []).filter((t) => !BULK_UNSUITABLE.has(t.id)),
    [templates.data],
  );

  const categoryCounts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const t of bulkSuitableTemplates) m[t.category ?? 'blog'] = (m[t.category ?? 'blog'] ?? 0) + 1;
    return m;
  }, [bulkSuitableTemplates]);

  const templatesInCategory = useMemo(
    () => bulkSuitableTemplates.filter((t) => (t.category ?? 'blog') === selectedCategory),
    [bulkSuitableTemplates, selectedCategory],
  );

  const selectedTemplate = bulkSuitableTemplates.find((t) => t.id === templateType);

  const pickCategory = (cat: string) => {
    if (cat === selectedCategory) return;
    setSelectedCategory(cat);
    const first = bulkSuitableTemplates.find((t) => (t.category ?? 'blog') === cat);
    if (first) setTemplateType(first.id);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    try {
      const res = await bulk.mutateAsync({
        keywords,
        site_id: siteId || null,
        tone,
        audience,
        template_type: templateType,
        section_length: sectionLength,
      });
      setResult({ created: res.created, hint: res.hint });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Toplu oluşturma başarısız');
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/content">
            <ArrowLeft className="w-4 h-4" /> İçeriklere dön
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold mt-2">Toplu Makale Oluştur</h1>
        <p className="text-muted-foreground text-sm">
          Her satıra bir anahtar kelime yaz — seçtiğin şablonla her biri için ayrı taslak oluşturulur.
          Sonra içerikler sayfasından tek tek <strong>Üret</strong> butonuna bas veya cron ile
          otomatize et. Kategori + etiket ataması dispatch anında yapılır.
        </p>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Anahtar Kelimeler</CardTitle>
            <CardDescription>Her satır = bir makale (max 200)</CardDescription>
          </CardHeader>
          <CardContent>
            <textarea
              rows={10}
              value={keywordText}
              onChange={(e) => setKeywordText(e.target.value)}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
              placeholder={`ev taşıma ipuçları\nkredi kartı başvurusu\nköpek bakımı`}
              required
            />
            <div className="text-xs text-muted-foreground mt-2">
              {keywords.length} / 200 kelime hazır
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">İçerik Türü</CardTitle>
            <CardDescription>
              Bulk modu tek-alan şablonlar için ideal. Karmaşık şablonlar (Karşılaştırma, URL yeniden yazım, Maç analizi) burada listelenmez — onları tek tek üret.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {templates.isPending ? (
              <div className="text-sm text-muted-foreground">Şablonlar yükleniyor…</div>
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  {CATEGORY_META.filter((c) => (categoryCounts[c.id] ?? 0) > 0).map((c) => {
                    const Icon = c.icon;
                    const active = selectedCategory === c.id;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => pickCategory(c.id)}
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                          active
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border bg-background hover:bg-accent',
                        )}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        {c.label}
                        <span
                          className={cn(
                            'ml-0.5 rounded-full px-1.5 py-0 text-[10px]',
                            active ? 'bg-primary-foreground/20' : 'bg-muted text-muted-foreground',
                          )}
                        >
                          {categoryCounts[c.id] ?? 0}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <select
                  value={templateType}
                  onChange={(e) => setTemplateType(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  {templatesInCategory.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </select>

                {selectedTemplate?.description && (
                  <p className="text-xs text-muted-foreground">{selectedTemplate.description}</p>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Varsayılanlar</CardTitle>
            <CardDescription>Tüm taslaklara uygulanır. Sonradan tek tek düzenlenebilir.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Hedef Site (opsiyonel)</Label>
                <select
                  value={siteId}
                  onChange={(e) => setSiteId(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">— Seçme —</option>
                  {sites.data?.sites.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Uzunluk</Label>
                <select
                  value={sectionLength}
                  onChange={(e) => setSectionLength(e.target.value as typeof sectionLength)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="short">Kısa (~600 kelime)</option>
                  <option value="medium">Orta (~1000 kelime)</option>
                  <option value="long" disabled={!features.long_articles}>
                    Uzun (~1800 kelime){!features.long_articles ? ' — eklenti gerekli' : ''}
                  </option>
                </select>
                {!features.long_articles && sectionLength === 'long' && (
                  <p className="text-xs text-destructive">
                    Uzun makale eklentin yok. <Link to="/packages" className="underline">Eklentiyi al</Link>.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Ton</Label>
                <input
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label>Hedef Kitle</Label>
                <input
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {error && (
          <div className="text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/30 px-3 py-2">
            {error}
          </div>
        )}
        {result && (
          <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold">{result.created} taslak oluşturuldu</p>
              <p className="text-xs text-muted-foreground">{result.hint}</p>
              <Button size="sm" className="mt-2" onClick={() => navigate({ to: '/content' })}>
                İçeriklere git
              </Button>
            </div>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Button type="submit" disabled={bulk.isPending || keywords.length === 0}>
            {bulk.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {bulk.isPending ? 'Oluşturuluyor…' : `${keywords.length} taslak oluştur`}
          </Button>
          <Button type="button" variant="outline" asChild>
            <Link to="/content">İptal</Link>
          </Button>
        </div>
      </form>
    </div>
  );
}
